export type FeedbackType = 'positive' | 'negative' | 'correction'

export interface FeedbackRequest {
    conversation_id: string
    message_id: string
    feedback_type: FeedbackType
    reason?: string
    correction_text?: string
    original_response?: string
}

export interface FeedbackResponse {
    feedback_id: string
    success: boolean
    message: string
    training_impact: string
}

const CSRF_COOKIE = '_eid_csrf_v1'
const CSRF_HEADER = 'X-CSRF-Token'

function csrfHeaders(): Record<string, string> {
    if (typeof document === 'undefined') return {}
    const match = document.cookie.match(
        new RegExp(`(?:^|;\\s*)${CSRF_COOKIE}=([^;]+)`),
    )
    const token = match ? decodeURIComponent(match[1]) : undefined
    return token ? { [CSRF_HEADER]: token } : {}
}

export async function submitFeedback(
    agentId: string,
    request: FeedbackRequest,
): Promise<FeedbackResponse> {
    const response = await fetch(
        `/api/crud/agents/${encodeURIComponent(agentId)}/training/feedback`,
        {
            method: 'POST',
            credentials: 'include',
            headers: {
                'Content-Type': 'application/json',
                Accept: 'application/json',
                ...csrfHeaders(),
            },
            body: JSON.stringify(request),
        },
    )

    if (!response.ok) {
        const error = await response.json().catch(() => ({
            message: 'Failed to submit feedback',
        }))
        throw new Error(error.message || `HTTP ${response.status}`)
    }

    return response.json()
}

export function submitPositiveFeedback(
    agentId: string,
    conversationId: string,
    messageId: string,
    originalResponse?: string,
): Promise<FeedbackResponse> {
    return submitFeedback(agentId, {
        conversation_id: conversationId,
        message_id: messageId,
        feedback_type: 'positive',
        original_response: originalResponse,
    })
}

export function submitNegativeFeedback(
    agentId: string,
    conversationId: string,
    messageId: string,
    originalResponse?: string,
): Promise<FeedbackResponse> {
    return submitFeedback(agentId, {
        conversation_id: conversationId,
        message_id: messageId,
        feedback_type: 'negative',
        original_response: originalResponse,
    })
}
